import { areJobsDisabled } from 'defs/flags';
import { group, groupEnd, log, warn } from 'helper/debug';
import guid from 'helper/guid';
import Storage, { StorageTypes } from 'lib/ClientStorage';
import moment from 'moment';

const win = window;

const isTestEnv = process.env.NODE_ENV === 'test';
export const JOB_CONTAINER_ID = 'impulsCoachJobContainer';

export const getJobContainer = (): { [key: string]: any } => {
	return (win[JOB_CONTAINER_ID] = win[JOB_CONTAINER_ID] || {});
};

export const JobTypes = {
	INTERVAL: '@@job/interval',
	TIMED: '@@job/timed',
};

const autoformatMs = (ms: number): string => {
	if (ms >= 0 && ms <= 100) {
		return `${ms}ms`;
	}

	if (ms > 100 && ms < 1000 * 60) {
		return `${ms / 1000}s`;
	}

	if (ms > 1000 * 60 && ms < 1000 * 60 * 60) {
		return `${moment.duration(ms).minutes()}min`;
	}

	return `${moment.duration(ms).hours()}h`;
};

const formatTimestamp = (unixTime: number): string => {
	return moment(unixTime).format('MM/DD/YYYY [at] HH:mm:ss');
};

const messages = {
	created: 'Job(%s): Created on %s [%s]',
	initial: 'Job(%s): initial call processing at %s',
	autostarted: 'Job(%s): Autostarted with the GUID %s',
	intervalJobRun: 'Job(%s): Started with an interval of %s',
	intervalJobStop: 'Job(%s): Identifier %s stopped',
	timedJobFoundLocalKey: 'TimedJob(...): Found existing timed job in storage with timestamp %s',
	timedJobExistingExceeded:
		'TimedJob(...): Existing timed job exceeds set interval time, stopping and restarting now.',
	timedJobStop: 'Job(%): Identifier %s stopped',
	stopAllJobs: 'Job: Stopping all %d jobs',
	notEnabled: "Jobs are disabled in your environement, set REACT_APP_DISABLE_JOBS to 'false' to re-enable all jobs.",
};

const delayedTimerCollection: any[] = [];

/*
 * Executes a method with a certain delay
 * @param {Function} fn
 * @param {number} [delay=1]
 * @return {void}
 */
export const delayed = (fn: Function, delay: number = 1) => {
	const timerId = win.setTimeout(() => {
		win.clearTimeout(timerId);
		return fn.call(undefined);
	}, delay);

	delayedTimerCollection.push(timerId);
	return timerId;
};

export const clearDelayedTimers = () => delayedTimerCollection.forEach((timerId) => win.clearTimeout(timerId));

export default class Job {
	public static jobList: Object[] = [];
	public static running: string[] = [];
	public static guids: string[] = [];

	public _id: string;
	public name: string;
	public type: string;
	public method: Function;
	public autostart: boolean;
	public settings: {
		interval: number;
		timer: number;
	};
	public timerId: number | void;
	public errors: any[] = undefined as any;
	public _initTimeStamp: number;
	public _startRunCount: number;
	public _jobExecutionCount: number;
	public _jobExectuionTimestamps: number[];

	constructor(
		name: string = '[anonym-job]',
		method: Function,
		type: string = JobTypes.INTERVAL,
		settings: Object = {},
		autostart: boolean = false
	) {
		group(`new Job(${name})`);
		this._id = guid();
		this.name = name;
		this._jobExecutionCount = 0;
		this._startRunCount = 0;
		this.timerId = void 0;
		this.type = type;
		this.method = method;
		this.settings = settings as any;
		this.autostart = !!autostart;
		this._jobExectuionTimestamps = [];
		this._initTimeStamp = Date.now();

		log(messages.created, name, formatTimestamp(this._initTimeStamp), this.type);

		if (autostart) {
			log(messages.autostarted, name, this._id);
			this.run();
			groupEnd(`new Job(${name})`);
		} else {
			groupEnd(`new Job(${name})`);
		}

		Job.jobList.push(this);
		Job.guids.push(this._id);

		getJobContainer()[this._id] = this;
	}
	/*
	 * Returns the GUID of the current job
	 * @return {string}         Job GUID
	 */
	get guid(): string {
		return this._id;
	}
	/*
	 * Check whether the job is running or not
	 * @type {boolean}
	 */
	get isRunning(): boolean {
		return !!this.timerId;
	}
	/*
	 * Returns a exection wrapper for the job which wraps
	 * the execution layer in a try catch and also increases
	 * the count for exectuion and timestamp save inside the instance.
	 * @type {Function}
	 */
	get executionWrapper(): Function {
		return (...args: any) => {
			try {
				this._jobExecutionCount++;
				this._jobExectuionTimestamps.push(Date.now());
				this.method.apply(this, [...args]);
			} catch (err) {
				this.errors.push(err || new Error('Unknown Error'));
			}
		};
	}

	get startCount(): number {
		return this._startRunCount;
	}

	get executionCount(): number {
		return this._jobExecutionCount;
	}

	get executionTimestamps(): number[] {
		return this._jobExectuionTimestamps;
	}
	/*
	 * Serialize the current task for e.g. saving to localStorage
	 * @type {Object}
	 */
	public serialize() {
		return {
			id: this._id,
			timestamp: this._initTimeStamp,
			autostart: !!this.autostart,
			type: this.type,
		};
	}

	public _removeJobFromRunningList() {
		const index = Job.running.indexOf(this._id);

		if (~index) {
			Job.running.splice(index, 1);
		}
	}
	/*
	 * @abstract
	 */
	public run() {
		/* @bastract */
	}
	/*
	 * @abstract
	 */
	public stop() {
		/* @bastract */
	}
	/*
	 * Deletes the job from the global container
	 * @return {boolean}        If action was succesfull
	 */
	public destroy(): boolean {
		try {
			delete getJobContainer()[this._id];
			return true;
		} catch (err) {
			return false;
		}
	}
}

// tslint:disable-next-line: max-classes-per-file
export class IntervalJob extends Job {
	/*
	 * Create an interval oriented Job task
	 * @param {string} name             Name of the Job
	 * @param {Function} method         Job Fn
	 * @param {number} interval         Timing setting
	 * @param {boolean} autostart       If the interval should start right now
	 * @param {boolean} initial       If the job should be executed initially
	 */
	constructor(
		name: string,
		method: Function,
		interval: number,
		autostart: boolean = false,
		initial: boolean = false
	) {
		super(name, method, JobTypes.INTERVAL, { interval }, autostart);

		if (initial) {
			delayed(() => {
				log(messages.initial, this.name, new Date().toISOString());
				(this.executionWrapper as any).call();
			}, 500);
		}
	}

	public run(): any {
		group(`IntervalJob(${this.name}).run()`);
		if (areJobsDisabled && !isTestEnv) {
			warn(messages.notEnabled);
			groupEnd(`IntervalJob(${this.name}).run()`);
			return null;
		}

		if (this.timerId) {
			this.stop();
		}

		log(messages.intervalJobRun, this.name, autoformatMs(this.settings.interval));
		this.timerId = win.setInterval(this.executionWrapper, this.settings.interval);
		this._startRunCount++;
		Job.running.push(this._id);
		groupEnd(`IntervalJob(${this.name}).run()`);
	}

	public stop() {
		if (this.timerId) {
			log(messages.intervalJobStop, this.name, this._id);
			win.clearInterval(this.timerId);
			this._removeJobFromRunningList();
			this.timerId = void 0;
		}
	}
}

const TimedJobStore = new Storage(StorageTypes.LOCAL);

// tslint:disable-next-line: max-classes-per-file
export class TimedJob extends Job {
	/*
	 * @param {string} name
	 * @param {Function} method
	 * @param {number} timer
	 * @param {boolean} autostart
	 */
	constructor(name: string, method: Function, timer: number, autostart: boolean = false) {
		super(name, method, JobTypes.TIMED, { timer }, autostart);
	}

	get storageKey(): string {
		return `impulsCoachTimedJob_${this.name}_${this.settings.timer}`;
	}

	public run(): any {
		if (areJobsDisabled && !isTestEnv) {
			return warn(messages.notEnabled, this.name);
		}

		const existingTimer: { timestamp: number; id: string } = TimedJobStore.get(this.storageKey);

		if (this.timerId || existingTimer) {
			log(messages.timedJobFoundLocalKey, existingTimer.timestamp);
			this._initTimeStamp = existingTimer.timestamp;
			this._id = existingTimer.id;
			if (existingTimer.timestamp + this.settings.timer > Date.now()) {
				log(messages.timedJobExistingExceeded);
				this.stop();
			}
		}

		TimedJobStore.set(this.storageKey, this.serialize());
		this.timerId = win.setTimeout(this.executionWrapper, this.settings.timer);
		this._startRunCount++;
		Job.running.push(this._id);
	}

	public stop() {
		TimedJobStore.set(this.storageKey, void 0);

		if (this.timerId) {
			log(messages.timedJobStop, this.name, this._id);
			win.clearTimeout(this.timerId);
			this._removeJobFromRunningList();
			this.timerId = void 0;
		}
	}
}

export const stopAllJobs = () => {
	log(messages.stopAllJobs, Job.jobList.length);

	(Job.jobList as Job[]).forEach((job: Job) => {
		job.stop();
	});
};
